/**
 * Home のアイコン／ウィジェットを並べるグリッド。
 *
 * 4 列を基準にし、アイコンは 1x1、ウィジェットは行×列で 1x1〜4x4 を占める。
 * 1 セルは正方形。行高は CSS では列幅に揃えられないので実測して入れる。
 * 編集モードでは並べ替え・サイズ変更・非表示ができ、結果は useHomeLayoutStore に保存する。
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Check, Minus, Plus, RotateCcw, Scaling } from 'lucide-react'
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { WidgetSizePicker } from '@/components/home/widget-size-picker'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { cn } from '@/lib/utils'
import { isWidgetSize, parseWidgetSize, useHomeLayoutStore, type WidgetSize } from '@/store/useHomeLayoutStore'

/** 実測が済むまでの行高。スマホの 4 列相当 */
const FALLBACK_CELL_SIZE = 84

/** 長押しで編集モードに入るまでの時間 */
const LONG_PRESS_MS = 600

/** これを超えて指が動いたらスクロールとみなして長押しをやめる */
const LONG_PRESS_TOLERANCE_PX = 10

export type HomeGridItem = {
  key: string
  label: string
  /** 既定の大きさ。省略するとアイコン扱い（1x1・サイズ変更なし） */
  defaultSize?: WidgetSize
  /** 大きさを変えさせず defaultSize のままにする */
  fixedSize?: boolean
  /** 既定では並べず、編集モードの「追加」から出させる */
  defaultHidden?: boolean
  render: (size: WidgetSize) => ReactNode
}

const isResizable = (item: HomeGridItem): boolean => Boolean(item.defaultSize) && !item.fixedSize

const resolveSize = (item: HomeGridItem, stored: WidgetSize | undefined): WidgetSize => {
  if (!item.defaultSize) return '1x1'
  if (item.fixedSize) return item.defaultSize
  if (stored && isWidgetSize(stored)) return stored
  return item.defaultSize
}

type SortableCellProps = {
  item: HomeGridItem
  size: WidgetSize
  isEditing: boolean
  onRemove: () => void
  onResize: () => void
}

const SortableCell = ({ item, size, isEditing, onRemove, onResize }: SortableCellProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
    disabled: !isEditing,
  })
  const span = parseWidgetSize(size)
  const canResize = isResizable(item)

  return (
    <div
      ref={setNodeRef}
      style={{
        gridColumn: `span ${span.cols}`,
        gridRow: `span ${span.rows}`,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn('relative min-w-0 touch-manipulation', isDragging && 'z-20 opacity-60')}
      {...(isEditing ? attributes : {})}
      {...(isEditing ? listeners : {})}
    >
      <div className={cn('flex h-full w-full justify-center', isEditing && 'home-wiggle pointer-events-none')}>
        {item.render(size)}
      </div>

      {isEditing && (
        <>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
            aria-label={`${item.label} を非表示にする`}
            className="absolute -top-1 -left-1 z-10 flex size-5 items-center justify-center rounded-full border-none bg-neutral-700 text-white shadow-md"
          >
            <Minus size={13} />
          </button>
          {canResize && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onResize}
              aria-label={`${item.label} の大きさを変える（現在 ${span.rows} 行 × ${span.cols} 列）`}
              className="absolute -right-1 -bottom-1 z-10 flex size-5 items-center justify-center rounded-full border-none bg-neutral-700 text-white shadow-md"
            >
              <Scaling size={12} />
            </button>
          )}
        </>
      )}
    </div>
  )
}

const EditBar = ({
  hiddenCount,
  onAdd,
  onReset,
  onDone,
}: {
  hiddenCount: number
  onAdd: () => void
  onReset: () => void
  onDone: () => void
}) => (
  <div className="sticky top-0 z-30 mx-3 mb-2 flex items-center gap-2 rounded-full border border-border/60 bg-background/90 px-3 py-2 backdrop-blur">
    <button
      type="button"
      onClick={onAdd}
      className="flex items-center gap-1 rounded-full border-none bg-muted px-3 py-1 text-xs active:opacity-70"
    >
      <Plus size={13} />
      追加
      {hiddenCount > 0 && <span className="text-muted-foreground">{hiddenCount}</span>}
    </button>
    <button
      type="button"
      onClick={onReset}
      className="flex items-center gap-1 rounded-full border-none bg-muted px-3 py-1 text-xs active:opacity-70"
    >
      <RotateCcw size={13} />
      リセット
    </button>
    <button
      type="button"
      onClick={onDone}
      className="ml-auto flex items-center gap-1 rounded-full border-none bg-sky-500 px-3 py-1 text-xs font-medium text-white active:opacity-70"
    >
      <Check size={13} />
      完了
    </button>
  </div>
)

export const HomeGrid = ({ items }: { items: HomeGridItem[] }) => {
  const order = useHomeLayoutStore((state) => state.order)
  const hidden = useHomeLayoutStore((state) => state.hidden)
  const storedSizes = useHomeLayoutStore((state) => state.sizes)
  const isEditing = useHomeLayoutStore((state) => state.isEditing)
  const setOrder = useHomeLayoutStore((state) => state.setOrder)
  const setSize = useHomeLayoutStore((state) => state.setSize)
  const dropSizes = useHomeLayoutStore((state) => state.dropSizes)
  const hide = useHomeLayoutStore((state) => state.hide)
  const show = useHomeLayoutStore((state) => state.show)
  const setEditing = useHomeLayoutStore((state) => state.setEditing)
  const reset = useHomeLayoutStore((state) => state.reset)

  const [isAddOpen, setIsAddOpen] = useState(false)
  // 閉じるアニメーションの間も中身を保つため、対象と開閉を別々に持つ
  const [resizeKey, setResizeKey] = useState<string | null>(null)
  const [isResizeOpen, setIsResizeOpen] = useState(false)
  const [cellSize, setCellSize] = useState(FALLBACK_CELL_SIZE)
  const gridRef = useRef<HTMLDivElement>(null)
  const longPressRef = useRef<number | null>(null)
  const pressOriginRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const element = gridRef.current
    if (!element) return

    const measure = () => {
      const [first] = getComputedStyle(element).gridTemplateColumns.split(' ')
      const width = Number.parseFloat(first)
      if (Number.isFinite(width) && width > 0) setCellSize(width)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const sizeMap = useMemo(() => new Map(storedSizes.map((entry) => [entry.key, entry.size])), [storedSizes])

  // 今は出ていないウィジェットの保存値まで消さないよう、対象は「今あって変えられないもの」に限る
  const fixedKeys = useMemo(() => items.filter((item) => !isResizable(item)).map((item) => item.key), [items])

  useEffect(() => {
    if (fixedKeys.length === 0) return
    dropSizes(fixedKeys)
  }, [fixedKeys, dropSizes])

  const { visible, hiddenItems } = useMemo(() => {
    const hiddenSet = new Set(hidden)
    const orderSet = new Set(order)
    // defaultHidden は「追加」で order に載るまで出さない
    const isHidden = (item: HomeGridItem) =>
      hiddenSet.has(item.key) || (item.defaultHidden === true && !orderSet.has(item.key))
    const byKey = new Map(items.map((item) => [item.key, item]))
    const ordered: HomeGridItem[] = []
    const seen = new Set<string>()

    for (const key of order) {
      const item = byKey.get(key)
      if (!item || isHidden(item) || seen.has(key)) continue
      ordered.push(item)
      seen.add(key)
    }
    for (const item of items) {
      if (seen.has(item.key) || isHidden(item)) continue
      ordered.push(item)
    }
    return { visible: ordered, hiddenItems: items.filter(isHidden) }
  }, [items, order, hidden])

  // タッチは PointerSensor だと縦の動きがスクロールに取られて pointercancel になるため、少し押さえてから掴ませる
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  )

  const resizeTarget = resizeKey === null ? null : (visible.find((item) => item.key === resizeKey) ?? null)

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const keys = visible.map((item) => item.key)
      const from = keys.indexOf(String(active.id))
      const to = keys.indexOf(String(over.id))
      if (from < 0 || to < 0) return
      setOrder(arrayMove(keys, from, to))
    },
    [visible, setOrder],
  )

  const handleAdd = useCallback(
    (key: string) => {
      // order が空のままだと show() の追加ぶんが先頭に来るので、今の並びを確定させてから足す
      setOrder([...visible.map((item) => item.key), key])
      show(key)
    },
    [visible, setOrder, show],
  )

  const cancelLongPress = useCallback(() => {
    pressOriginRef.current = null
    if (longPressRef.current === null) return
    window.clearTimeout(longPressRef.current)
    longPressRef.current = null
  }, [])

  const startLongPress = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (isEditing) return
      cancelLongPress()
      pressOriginRef.current = { x: event.clientX, y: event.clientY }
      longPressRef.current = window.setTimeout(() => setEditing(true), LONG_PRESS_MS)
    },
    [isEditing, cancelLongPress, setEditing],
  )

  const trackLongPress = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = pressOriginRef.current
      if (!origin) return
      const moved = Math.hypot(event.clientX - origin.x, event.clientY - origin.y)
      if (moved > LONG_PRESS_TOLERANCE_PX) cancelLongPress()
    },
    [cancelLongPress],
  )

  const grid = (
    <div
      ref={gridRef}
      className="mx-3 grid grid-cols-4 gap-2.5 sm:grid-cols-6 lg:grid-cols-8"
      style={{ gridAutoRows: `${cellSize}px` }}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerMove={trackLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={(e) => e.preventDefault()}
    >
      {visible.map((item) => {
        const size = resolveSize(item, sizeMap.get(item.key))
        return (
          <SortableCell
            key={item.key}
            item={item}
            size={size}
            isEditing={isEditing}
            onRemove={() => hide(item.key)}
            onResize={() => {
              setResizeKey(item.key)
              setIsResizeOpen(true)
            }}
          />
        )
      })}
    </div>
  )

  return (
    <>
      {isEditing && (
        <EditBar
          hiddenCount={hiddenItems.length}
          onAdd={() => setIsAddOpen(true)}
          onReset={reset}
          onDone={() => setEditing(false)}
        />
      )}

      {isEditing ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map((item) => item.key)} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}

      <BottomSheet title="ホームに追加" open={isAddOpen} onOpenChange={setIsAddOpen} snapPoints={[0.7]}>
        <div className="flex flex-col gap-1 px-4 pb-6">
          {hiddenItems.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">追加できるものはありません</p>
          ) : (
            hiddenItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handleAdd(item.key)}
                className="flex items-center justify-between rounded-xl border-none bg-muted/50 px-4 py-3 text-left active:opacity-70"
              >
                <span className="truncate text-sm">{item.label}</span>
                <Plus size={16} className="shrink-0 text-sky-400" />
              </button>
            ))
          )}
        </div>
      </BottomSheet>

      {resizeTarget && (
        <WidgetSizePicker
          open={isResizeOpen}
          onOpenChange={setIsResizeOpen}
          label={resizeTarget.label}
          size={resolveSize(resizeTarget, sizeMap.get(resizeTarget.key))}
          onSelect={(next) => setSize(resizeTarget.key, next)}
        />
      )}
    </>
  )
}
